import { useContext, useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AuthContext } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import ProspectDossier from "@/components/ProspectDossier";
import { Play, RefreshCw, BarChart3 } from "lucide-react";

import { CompanyHeader } from "@/components/company/CompanyHeader";
import { BusinessOverview } from "@/components/company/BusinessOverview";
import { SalesPlaybook } from "@/components/company/SalesPlaybook";
import { AuditSections } from "@/components/company/AuditSections";
import { GbpVerifyDialog } from "@/components/company/GbpVerifyDialog";

export default function CompanyDetailPage() {
  const { companyId } = useParams();
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [auditing, setAuditing] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [team, setTeam] = useState([]);
  const [downloading, setDownloading] = useState("");
  const [gbpOpen, setGbpOpen] = useState(false);
  const [gbpUrl, setGbpUrl] = useState("");
  const [gbpRating, setGbpRating] = useState("");
  const [gbpReviews, setGbpReviews] = useState("");
  const [gbpSaving, setGbpSaving] = useState(false);

  const verifyGbp = async () => {
    if (!gbpUrl.trim()) { toast.error("Paste your Google Business Profile / Maps link"); return; }
    setGbpSaving(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/gbp-verify`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({
          gbp_url: gbpUrl.trim(),
          rating: gbpRating ? parseFloat(gbpRating) : null,
          reviews: gbpReviews ? parseInt(gbpReviews, 10) : null,
        }),
      });
      if (res.ok) {
        setCompany(await res.json());
        setGbpOpen(false);
        toast.success("Google Business Profile verified");
        fetchCompany();
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || "Could not verify link");
      }
    } catch { toast.error("Could not verify link"); }
    finally { setGbpSaving(false); }
  };

  const confirmGbp = async () => {
    if (!company?.gbp_url) return;
    setGbpSaving(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/gbp-verify`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({
          gbp_url: company.gbp_url,
          rating: company.google_rating || null,
          reviews: company.google_reviews || null,
        }),
      });
      if (res.ok) { setCompany(await res.json()); toast.success("Google Business Profile confirmed"); }
      else { toast.error("Could not confirm"); }
    } catch { toast.error("Could not confirm"); }
    finally { setGbpSaving(false); }
  };

  const clearGbp = async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/gbp-verify`, { method: "DELETE", credentials: "include" });
      if (res.ok) { setCompany(await res.json()); toast.success("GBP cleared"); }
    } catch { toast.error("Failed to remove"); }
  };

  const downloadReport = async (format, auditId) => {
    setDownloading(format);
    try {
      const suffix = format === "html" ? "?download=true" : "";
      const res = await fetch(`${API}/reports/${auditId}/${format}${suffix}`, { credentials: "include" });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(company?.name || "report").replace(/[^a-z0-9]+/gi, "_")}_Audit.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} report downloaded`);
    } catch {
      toast.error(`Failed to generate ${format.toUpperCase()}`);
    } finally {
      setDownloading("");
    }
  };

  const fetchCompany = useCallback(async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}`, { credentials: "include" });
      if (res.ok) setCompany(await res.json());
      else { toast.error("Not found"); navigate("/prospects"); }
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [API, companyId, navigate]);

  const fetchTeam = useCallback(async () => {
    try {
      const res = await fetch(`${API}/users`, { credentials: "include" });
      if (res.ok) setTeam((await res.json()).users || []);
    } catch (e) { console.error("Team fetch error:", e); }
  }, [API]);

  useEffect(() => { fetchCompany(); }, [fetchCompany]);
  useEffect(() => { if (user?.role === "admin") fetchTeam(); }, [user, fetchTeam]);

  // Auto-refresh while a deep analysis is in progress (whether started here or
  // already running when the page loaded). Stops on completion/failure, and
  // surfaces a retry hint if the run stalls (e.g. its background task died on a
  // server restart, leaving it stuck on a stage).
  useEffect(() => {
    if (company?.dossier_status !== "running") return;
    let cancelled = false;
    let attempts = 0;
    const iv = setInterval(async () => {
      attempts += 1;
      try {
        const res = await fetch(`${API}/companies/${companyId}`, { credentials: "include" });
        if (!res.ok || cancelled) return;
        const c = await res.json();
        if (cancelled) return;
        setCompany(c);
        if (c.dossier_status === "completed") {
          clearInterval(iv); setAnalyzing(false); toast.success("Prospect Dossier ready!");
        } else if (c.dossier_status === "failed") {
          clearInterval(iv); setAnalyzing(false);
          toast.error(c.dossier_error || "Deep analysis failed — click Re-analyze to retry");
        } else if (attempts > 50) {  // ~2.5 min with no completion → treat as stalled
          clearInterval(iv); setAnalyzing(false);
          toast("This analysis seems to have stalled. Click Re-analyze to retry.", { icon: "⚠️" });
        }
      } catch { /* transient — keep polling */ }
    }, 3000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [company?.dossier_status, API, companyId]);

  const assignTo = async (userId) => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/assign`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ user_id: userId }),
      });
      if (res.ok) {
        const data = await res.json();
        setCompany((p) => ({ ...p, assigned_to: data.assigned_to, assignment_status: data.assignment_status }));
        const name = team.find((t) => t.user_id === userId)?.name || "rep";
        toast.success(`Lead assigned to ${name}`);
      } else { toast.error("Assign failed"); }
    } catch { toast.error("Assign failed"); }
  };

  const runAudit = async () => {
    setAuditing(true);
    try {
      const res = await fetch(`${API}/audit/run`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ company_id: companyId, types: ["website", "presence"] }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success("Audit running...");
        let att = 0;
        let done = false;
        const poll = setInterval(async () => {
          if (done) return;
          att++;
          try {
            const a = await (await fetch(`${API}/audit/${data.audit_id}`, { credentials: "include" })).json();
            if (done) return;
            if (a.status === "completed") { done = true; clearInterval(poll); setAuditing(false); toast.success("Done!"); fetchCompany(); }
            else if (a.status === "failed" || att > 30) { done = true; clearInterval(poll); setAuditing(false); toast.error("Failed"); }
          } catch { /* retry */ }
        }, 2000);
      }
    } catch { toast.error("Failed"); setAuditing(false); }
  };

  const deepAnalyze = async () => {
    setAnalyzing(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/deep-analyze`, { method: "POST", credentials: "include" });
      if (!res.ok) { toast.error("Could not start deep analysis"); setAnalyzing(false); return; }
      toast.success("Deep analysis started — researching across sources…");
      // Flip to running locally; the status-driven poll below auto-refreshes the page.
      setCompany((p) => ({ ...p, dossier_status: "running", dossier_stage: "crawling" }));
    } catch { toast.error("Deep analysis failed"); setAnalyzing(false); }
  };

  const syncCRM = async (platform) => {
    setSyncing(true);
    try {
      const res = await fetch(`${API}/crm/sync`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ company_id: companyId, platform }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || "Sync failed"); return; }
      if (platform === "monday") {
        const extras = [data.note_added && "note", data.pdf_attached && "dossier PDF"].filter(Boolean);
        toast.success(`Synced to Monday board "${data.board_name}"${extras.length ? ` (+ ${extras.join(" + ")})` : ""}`);
      } else {
        toast.success("Synced to GHL");
      }
      fetchCompany();
    } catch { toast.error("Sync failed"); }
    finally { setSyncing(false); }
  };

  const updateStatus = async (status) => {
    await fetch(`${API}/companies/${companyId}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      credentials: "include", body: JSON.stringify({ status }),
    });
    setCompany((p) => ({ ...p, status }));
    toast.success(`Status: ${status}`);
  };

  if (loading) return <div className="flex items-center justify-center h-48"><RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  if (!company) return null;

  const latestAudit = (company.audits || []).find((a) => a.status === "completed");
  const wChecks = latestAudit?.website_checks || [];
  const pChecks = latestAudit?.presence_checks || [];
  const fails = wChecks.filter((c) => c.status === "fail");
  const warns = wChecks.filter((c) => c.status === "warn");
  const passes = wChecks.filter((c) => c.status === "pass");
  const missingP = pChecks.filter((c) => c.status === "missing");
  const foundP = pChecks.filter((c) => c.status === "found");
  const inaccP = pChecks.filter((c) => c.status === "inaccurate");
  const unknownP = pChecks.filter((c) => c.status === "unknown");
  const gbpCheck = pChecks.find((c) => c.directory === "Google Business Profile");
  // GBP is "found" if the presence check confirms it, or we hold a Maps link /
  // review data (Places or Serper-Maps search populated these). Used so the banner
  // shows a positive state instead of the "couldn't auto-detect" prompt.
  const gbpFound = gbpCheck?.status === "found" || !!company.gbp_url || (company.google_reviews || 0) > 0;
  const playbook = company.sales_playbook;
  const searchRankings = company.search_rankings;
  const assignedName = team.find((t) => t.user_id === company.assigned_to)?.name;

  // Group web checks
  const catGroups = {};
  wChecks.forEach((c) => { if (!catGroups[c.category]) catGroups[c.category] = []; catGroups[c.category].push(c); });

  // Reflect an in-progress analysis in the UI even when the page was just opened
  // mid-run (not only when the user clicked Deep Analyze this session).
  const isAnalyzing = analyzing || company.dossier_status === "running";

  return (
    <div data-testid="company-detail-page" className="max-w-5xl mx-auto space-y-4 animate-fade-in-up">

      <CompanyHeader
        company={company} user={user} team={team} assignedName={assignedName} latestAudit={latestAudit}
        analyzing={isAnalyzing} auditing={auditing} syncing={syncing} downloading={downloading}
        navigate={navigate} assignTo={assignTo} updateStatus={updateStatus}
        deepAnalyze={deepAnalyze} runAudit={runAudit} downloadReport={downloadReport} syncCRM={syncCRM} />

      <BusinessOverview company={company} latestAudit={latestAudit} fails={fails} warns={warns} passes={passes} />

      {/* Prospect Dossier (deep multi-source intelligence) */}
      <ProspectDossier company={company} API={API} onAnalyze={deepAnalyze} analyzing={isAnalyzing} />

      <SalesPlaybook playbook={playbook} />

      <AuditSections
        company={company} latestAudit={latestAudit} wChecks={wChecks} pChecks={pChecks}
        fails={fails} warns={warns} passes={passes} foundP={foundP} inaccP={inaccP} missingP={missingP} unknownP={unknownP}
        catGroups={catGroups} gbpFound={gbpFound} searchRankings={searchRankings}
        gbpSaving={gbpSaving} confirmGbp={confirmGbp} clearGbp={clearGbp} setGbpUrl={setGbpUrl} setGbpOpen={setGbpOpen} />

      {/* No audit CTA */}
      {!latestAudit && (
        <Card className="border-dashed border-2 border-slate-300 shadow-none">
          <CardContent className="py-8 text-center">
            <BarChart3 className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm font-medium text-muted-foreground">No audit data yet</p>
            <p className="text-xs text-muted-foreground mb-3">Run an audit to see website scores, presence checks, AI analysis, and a personalized sales playbook</p>
            <Button onClick={runAudit} disabled={auditing} className="bg-indigo-600 hover:bg-indigo-700 text-white">
              {auditing ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />} Run Full Audit
            </Button>
          </CardContent>
        </Card>
      )}

      <GbpVerifyDialog
        open={gbpOpen} onOpenChange={setGbpOpen}
        gbpUrl={gbpUrl} setGbpUrl={setGbpUrl} gbpRating={gbpRating} setGbpRating={setGbpRating}
        gbpReviews={gbpReviews} setGbpReviews={setGbpReviews} gbpSaving={gbpSaving} verifyGbp={verifyGbp} />
    </div>
  );
}
